<template>
  <div
    class="rs-form-calendar-custom"
    :style="bottomMarginStyle"
  >
    <label
      v-if="label"
      :for="id"
    >
      {{ label }}
      <sup v-if="required">*</sup>
    </label>
    <span v-if="helpTooltip">
      <RsIcon
        :id="`helperTooltip-${id}`"
        icon="question-circle"
        class="tooltip-question-icon"
      />
      <RsTooltip
        :target="`helperTooltip-${id}`"
        :title="helpTooltip"
        :position="helpTooltipPosition"
        :delay="{ show: 200 }"
      />
    </span>
    <ValidationProvider
      :id="id"
      v-slot="{ errors }"
      class="veeValidate"
      :class="getCustomClass(id)"
      tag="div"
      :name="label ? label : 'Date'"
      mode="aggressive"
      :rules="dateRules"
    >
      <BInputGroup
        :class="iconOnly ? 'icon-only-input-group' : ''"
      >
        <MaskedInput
          v-if="!iconOnly && !monthYearOnly"
          v-model="input"
          mask="11/11/1111"
          :placeholder="$ts('calendar.inputPlaceholder')"
          class="form-control"
          :class="{ 'disable-typing': disableTyping }"
          :disabled="disabled || disableTyping"
          @blur.native="handleInputBlur"
          @focus.native="handleInputFocus"
        />
        <MaskedInput
          v-else-if="!iconOnly"
          v-model="yearMonthFormatted"
          mask="1111/11"
          :placeholder="value"
          class="form-control"
          :disabled="disabled"
          @blur.native="handleInputBlur"
        />
        <BInputGroupAppend>
          <BDropdown
            ref="calendarDropdown"
            :disabled="disabled"
            right
            no-caret
            variant="primary"
          >
            <template #button-content>
              <i class="far fa-calendar rs-text-white" />
            </template>
            <div class="row-calendar bg-white px-3 py-2 text-center">
              <h5 v-show="customPlaceholder || input">
                {{ `${customPlaceholder && input === 'Invalid Date' ? customPlaceholder : input}` }}
              </h5>
              <h5 v-show="!input">
                {{ $ts('calendar.selectDate') }}
              </h5>
              <div
                class="row justify-content-center mb-3"
              >
                <RsButton
                  v-show="!monthYearOnly"
                  icon="chevron-left"
                  variant="info"
                  :class="{ 'not-allowed': arrowBlocked }"
                  @click="handleMonthChange('desc')"
                />

                <RsFormSelect
                  id="calendarMonth"
                  v-model="calendar.month"
                  class="ffCalendar mb-0 px-1"
                  :options="localizedMonths"
                  @input="handleMonthYearChange(`${calendar.year}-${calendar.month}-01`)"
                />
                <RsFormSelect
                  id="calendarYear"
                  v-model="calendar.year"
                  class="ffCalendar mb-0 pr-1"
                  :options="years"
                  @input="handleMonthYearChange(`${calendar.year}-${calendar.month}-01`)"
                />

                <RsButton
                  v-show="monthYearOnly"
                  icon="check"
                  variant="success"
                  weight="fas"
                  @click="handleMonthYearOnlyChange"
                />

                <RsButton
                  v-show="!monthYearOnly"
                  icon="chevron-right"
                  variant="info"
                  :class="{ 'not-allowed': arrowBlocked }"
                  @click="handleMonthChange('asc')"
                />
              </div>
              <div
                v-for="(day) in localizedDaysOfWeek"
                v-show="!monthYearOnly"
                :key="`day-of-week-${day}`"
                class="col-calendar-weekday small"
              >
                {{ day }}
              </div>
              <div
                v-show="!monthYearOnly"
                class="container-calendar"
              >
                <div
                  v-for="(day, index) in calendar.days"
                  :key="`${index}-${calendar.year}-${calendar.month}-${day}`"
                  class="col-calendar border font-weight-bold"
                  :style="day > 0 && day < 100 ? 'cursor:pointer' : 'background:#f8f8f8'"
                  :class="{ 'bg-primary text-white': isSelectedDay(day), 'bg-gray disabled not-allowed': isBlocked(day) }"
                  @click="handleCalendarInput(`${calendar.year}-${calendar.month}-${day}`)"
                >
                  <span v-show="day > 0 && day < 100">{{ day }}</span>
                </div>
              </div>
              <div class="footer-text">
                <span>{{ footerText }}</span>
              </div>
            </div>
          </BDropdown>
        </BInputGroupAppend>
      </BInputGroup>
      <div
        v-show="!hideHelp && showHelp"
        class="text-muted pt-1 px-1 helper"
      >
        {{ $ts('calendar.formatHint') }}
      </div>
      <div
        v-if="(errors.length || customErrorMessage) && !hideErrors"
        class="text--error"
      >
        {{ errors[0] || customErrorMessage }}
      </div>
    </ValidationProvider>
  </div>
</template>

<script lang="ts">
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { ValidationProvider, extend } from 'vee-validate';
import { defineComponent, PropType } from 'vue';
import MaskedInput from 'vue-masked-input';
import { BDropdown } from 'bootstrap-vue';
import { ISelectOption } from '@/shared';
import { isValidSQLDate } from '@/shared/helpers/date-helpers';
import { monthsOptions as months, daysOfWeek } from '@/shared/helpers/static-select-options';
import i18n from '@/i18n';

extend('betweenYears', {
  validate: (value: any, { yearMinOffset, yearMaxOffset, minDate, maxDate }: Record<string, any>) => {
    dayjs.extend(isSameOrBefore);
    dayjs.extend(isSameOrAfter);
    const now = dayjs();
    const input = dayjs(value);
    const minYear = now.year() - yearMinOffset;
    const maxYear = now.year() + yearMaxOffset;
    const _minDate = dayjs(minDate);
    const _maxDate = dayjs(maxDate);
    return input.isValid() && (input.year() >= minYear && input.year() <= maxYear) && (input.isSameOrAfter(_minDate, 'day') && input.isSameOrBefore(_maxDate, 'day'));
  },
  params: ['yearMinOffset', 'yearMaxOffset', 'minDate', 'maxDate'],
  message: () => i18n.t('validation.dateOutOfRange') as string,
});
extend('length', {
  validate: (value: any, { length, required }: Record<string, any>) => {
    if (!value && !required) {
      return true;
    }

    return value.length === length;
  },
  params: ['length', 'required'],
  message: () => i18n.t('validation.dateFormat') as string,
});

extend('isValidSQLDate', {
  validate: isValidSQLDate,
  message: () => i18n.t('validation.dateInvalid') as string,
});

type TMonthValue = ''|'01'|'02'|'03'|'04'|'05'|'06'|'07'|'08'|'09'|'10'|'11'|'12';

const now = dayjs();

const getSelectYears = (minOffset: number, maxOffset: number) : ISelectOption[] => {
  let yearLow = now.year() - minOffset;
  const yearHigh = now.year() + maxOffset;
  const returnArr: ISelectOption[] = [];
  while (yearLow <= yearHigh) {
    returnArr.push({ value: yearLow, text: `${yearLow}` });
    yearLow += 1;
  }

  return returnArr;
};

const getDaysInMonth = (currentDate: Dayjs): number[] => {
  const days = (currentDate).daysInMonth();
  const daysAsArr: number[] = [];
  let num = 1;
  while (num <= days) {
    daysAsArr.push(num);
    num += 1;
  }

  const weekDayOfMonthFirstDay = dayjs(`${currentDate.format('YYYY')}-${currentDate.format('MM')}-01`).day();
  const weekDayOfMonthLastDay = dayjs(`${currentDate.format('YYYY')}-${currentDate.format('MM')}-${num}`).day();

  num = 0;
  while (weekDayOfMonthFirstDay + num !== 0) {
    daysAsArr.unshift(num);
    num -= 1;
  }

  num = weekDayOfMonthLastDay;

  if (num !== 0) {
    while (num < 7) {
      daysAsArr.push(num * 100);
      num += 1;
    }
  }

  return daysAsArr;
};

export default defineComponent({
  name: 'RsFormCalendarCustom',

  components: {
    ValidationProvider,
    MaskedInput,
  },

  props: {
    rules: {
      type: Object,
      default: () => {},
    },
    id: {
      type: String,
      default: 'rs-date-picker-v2-1',
    },
    label: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number, Date, Object] as PropType<string | number | Date | Dayjs | null>,
      default: '',
    },
    monthYearOnly: {
      type: Boolean,
      default: false,
    },
    listingScoresHeart: {
      type: Boolean,
      default: false,
    },
    outputFormat: {
      type: String,
      default: 'YYYY-MM-DD',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    yearMinOffset: {
      type: Number,
      default: 20,
    },
    yearMaxOffset: {
      type: Number,
      default: 20,
    },
    customBottomMargin: {
      type: String,
      default: '',
    },
    footerText: String,
    minDate: {
      type: String,
      default: dayjs().subtract(50, 'year').toString(),
    },
    maxDate: {
      type: String,
      default: dayjs().add(50, 'year').toString(),
    },
    availableDates: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    blockPreviousDates: {
      type: Boolean,
      default: true,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
    hideErrors: {
      type: Boolean,
      default: false,
    },
    hideHelp: {
      type: Boolean,
      default: false,
    },
    disableTyping: {
      type: Boolean,
      default: false,
    },
    /**
     * This is used to add custom styles on a case-by-case basis to the parent component
     * where this component is implemented.
     *
     * Pass an array of element keys, usually the IDs of the targeted elements, to be used in
     * the "getCustomClass" function.
     *
     * Update the "getCustomClass" function to add id-class mappings.
    */
    customStyleKeys: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    customPlaceholder: {
      type: String,
      default: '',
    },
    /**
     * Set this to true if monthYearOnly format is true and there has to be
     * either minimum month or minimum year (provide yearMinOffset to calculate
     * for minimum year).
    */
    hasMonthAndYearOnlyMin: {
      type: Boolean,
      default: false,
    },
    /**
     * Minimum month for monthYearOnly format
    */
    monthAndYearOnlyMinMonth: {
      type: String as PropType<TMonthValue>,
      default: '',
    },
    /** Determines if null date is considered as a valid value */
    isNullable: {
      type: Boolean,
      default: false,
    },
    /**
     * Because we have some cases where we need to force the rules to be applied, even without the input value
    */
    forceRules: {
      type: Boolean,
      default: false,
    },
    helpTooltip: {
      type: String,
      default: '',
    },
    helpTooltipPosition: {
      type: String,
      default: 'top',
      validator: (value: string) => ['top', 'right', 'bottom', 'left'].includes(value),
    },
  },

  data() {
    return {
      months,
      years: getSelectYears(this.yearMinOffset, this.yearMaxOffset),
      input: '' as string | null,
      customErrorMessage: null as string | null,
      arrowBlocked: false as boolean,
      showHelp: false as boolean,
      calendar: {
        daysOfWeek,
        month: now.format('MM'),
        day: now.format('DD'),
        year: now.format('YYYY'),
        days: getDaysInMonth(now),
      },
    };
  },

  computed: {
    /** Month names localized to the current i18n locale, preserving disabled state from this.months. */
    localizedMonths(): ISelectOption[] {
      const { locale } = this.$i18n;
      return this.months.map((month) => ({
        ...month,
        text: new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2000, Number(month.value) - 1, 1)),
      }));
    },
    /** Day-of-week abbreviations localized to the current i18n locale. */
    localizedDaysOfWeek(): string[] {
      const { locale } = this.$i18n;
      return Array.from({ length: 7 }, (_, i) => {
        // i=0 → Sunday (2000-01-02 is a Sunday)
        const date = new Date(2000, 0, 2 + i);
        return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
      });
    },
    yearMonthFormatted: {
      get(): string {
        return this.customPlaceholder && !this.value ? this.customPlaceholder : dayjs(this.value).format('YYYY-MM');
      },
      set(newValue: string): void {
        this.input = this.customPlaceholder && !newValue ? this.customPlaceholder : dayjs(newValue).format('YYYY-MM');
      },
    },

    bottomMarginStyle(): string {
      if (this.customBottomMargin) {
        return `margin-bottom: ${this.customBottomMargin}`;
      }
      return 'margin-bottom: 1rem';
    },
    dateRules() {
      if (
        (!this.monthYearOnly && this.input && !this.input.startsWith('_') && !this.input.endsWith('_'))
        || this.forceRules
      ) {
        return {
          ...this.rules,
          isValidSQLDate: this.input,
          required: this.required,
          length: { required: this.required, length: 10 },
          betweenYears: {
            yearMinOffset: this.yearMinOffset,
            yearMaxOffset: this.yearMaxOffset,
            minDate: this.minDate,
            maxDate: this.maxDate,
          },
        };
      }
      return '';
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(newValue: string): void {
        this.customErrorMessage = null;
        this.input = newValue;

        if (this.isNullable && newValue === null) {
          this.input = newValue;
        }
        if (!this.isNullable && newValue !== null && newValue === '') {
          this.setCalendarDate(dayjs());
          this.input = '';
        }
        if (newValue !== null && isValidSQLDate(newValue)) {
          const currentDate = dayjs(newValue);
          this.setCalendarDate(currentDate);
          this.input = currentDate.format('MM/DD/YYYY');
        }
        if (this.input && this.input !== '' && !isValidSQLDate(this.input)) {
          this.customErrorMessage = i18n.t('validation.dateInvalid') as string;
        }
      },
    },

    'calendar.month': {
      handler() {
        const date = dayjs(`${this.calendar.year}-${this.calendar.month}-01`);
        this.calendar.days = getDaysInMonth(date);
      },
    },

    'calendar.year': {
      handler() {
        const date = dayjs(`${this.calendar.year}-${this.calendar.month}-01`);
        this.setAvailableMonthOptions();
        this.calendar.days = getDaysInMonth(date);
      },
    },
    monthAndYearOnlyMinMonth: {
      handler() {
        this.setAvailableMonthOptions();
        this.years = getSelectYears(this.yearMinOffset, this.yearMaxOffset);
      },
    },
    yearMinOffset: {
      handler() {
        this.years = getSelectYears(this.yearMinOffset, this.yearMaxOffset);
      },
    },
  },
  mounted() {
    if (this.hasMonthAndYearOnlyMin) {
      this.setAvailableMonthOptions();
      this.setMonthAndYearOnlyMin();
    }
  },

  methods: {
    /**
     * Sets the available month options based on the selected year and minimum available year.
     * If the selected year is the minimum available year, only months from the minimum month onwards are available.
     * Otherwise, all months are available. This is specifically used in the monthAndYearOnly format.
    */
    setAvailableMonthOptions() {
      const isMinimumYearSelected = Number(this.calendar.year) === (now.year() - this.yearMinOffset);
      if (this.hasMonthAndYearOnlyMin && this.monthAndYearOnlyMinMonth && isMinimumYearSelected) {
        const minMonthIndex = this.months.findIndex((month) => month.value === this.monthAndYearOnlyMinMonth);
        this.months = months.map((month, index) => {
          if (index < minMonthIndex) {
            return {
              ...month,
              disabled: true,
            };
          }
          return month;
        });
      } else {
        this.months = months;
      }
    },
    /**
     * Sets minimum month override for monthAndYearOnly format
    */
    setMonthAndYearOnlyMin() {
      this.calendar = {
        ...this.calendar,
        month: this.monthAndYearOnlyMinMonth ?? now.format('MM'),
      };
    },
    /**
     * Maps customStyleKeys, an array of element IDs, to their custom classes.
     * @param id - Id of the targeted element
     * @returns String - Custom class(es) to be applied to the element with the passed id.
    */
    getCustomClass(id: string): string {
      const customClassMap: { [key: string]: string } = {
        // The key is the id of the targeted element, while value can be a class or multiple classes separated by spaces.
        'premium-reporting-start-month': 'dt-calendar-absolute',
        'premium-reporting-end-month': 'dt-calendar-absolute',
        // Add more mappings as needed
      };
      return this.customStyleKeys.filter((key) => id === key).map((key) => customClassMap[key])[0] ?? '';
    },
    handleInputBlur(e: Event): void {
      const val: string = e.target ? (e.target as HTMLInputElement).value : '';

      // set calendar month and year in case the input is manual input
      const date = val.split('/');

      // mm/dd/yyyy
      if (this.monthYearOnly) {
        this.calendar.month = date[0] ?? this.calendar.month;
        this.calendar.year = date[1] ?? this.calendar.year;
      } else {
        this.calendar.month = date[0] ?? this.calendar.month;
        this.calendar.day = date[1] ?? this.calendar.day;
        this.calendar.year = date[2] ?? this.calendar.year;
      }

      this.showHelp = false;

      // Full calendar length MM/DD/YYYY
      if (val && val.length === 10) {
        if (val.startsWith('__')) {
          this.input = this.isNullable ? null : '';
          this.$emit('input', this.input);
        } else {
          const dateVal = dayjs(val);
          if (dateVal.isValid() && !this.isBlocked(dayjs(dateVal).date())) {
            this.$emit('input', val);
            this.setCalendarDate(dateVal);
            this.input = dateVal.format('MM/DD/YYYY');
          } else {
            this.$emit('input', null);
          }
        }
      } else if (this.monthYearOnly) {
        // Validate year and month and handle its change
        if (Number(date[0]) > 1900 && Number(date[1]) <= 12) {
          this.calendar.year = date[0] ?? this.calendar.year;
          this.calendar.month = date[1] ?? this.calendar.month;
          this.handleMonthYearOnlyChange();
        } else if (val !== '' && this.value !== val) {
          // Show error message
          this.customErrorMessage = i18n.t('validation.dateInvalid') as string;
        } else {
          this.customErrorMessage = null;
          this.$emit('input', dayjs(`${this.calendar.year}-${this.calendar.month}-01`).format('YYYY-MM'));
        }
      } else {
        this.$emit('input', null);
      }
    },

    handleInputFocus(e: Event): void {
      this.showHelp = true;
    },

    handleCalendarInput(data: string): void {
      const date = dayjs(data);
      if (!this.isBlocked(date.date())) {
        this.input = date.format('MM/DD/YYYY');
        this.$emit('input', data);
        this.setCalendarDate(date);
        this.close();
      }
    },

    handleMonthChange(direction: 'asc' | 'desc'): void {
      let date = dayjs(`${this.calendar.year}-${this.calendar.month}-01`);
      date = direction === 'asc' ? date.add(1, 'month') : date.subtract(1, 'month');
      if (!this.years.find((y) => y.value === dayjs(date).year())) {
        this.arrowBlocked = true;
        return;
      }
      this.setCalendarDate(date);
      this.arrowBlocked = false;
      this.$emit('update-month', `${this.calendar.year}-${this.calendar.month}`);
    },

    handleMonthYearOnlyChange(): void {
      this.close();
      const date = dayjs(`${this.calendar.year}-${this.calendar.month}-01`);
      this.input = date.format('MM/YYYY');
      this.$emit('input', `${this.calendar.year}-${this.calendar.month}`);
      this.setCalendarDate(date);
    },

    handleMonthYearChange(data: string): void {
      this.$emit('update-month', `${this.calendar.year}-${this.calendar.month}`);
    },

    isSelectedDay(day: number): boolean {
      return day > 0 && day < 32 && dayjs(this.input).isSame(dayjs(`${this.calendar.year}-${this.calendar.month}-${day}`, 'day'));
    },

    isBlocked(day: number): boolean {
      // Now let's check if the previous dates should also be blocked
      const blocked = !this.blockPreviousDates && dayjs(`${this.calendar.year}-${this.calendar.month}-${day}`).isAfter(dayjs());
      return (day > 0 && day < 32
        && (dayjs(`${this.calendar.year}-${this.calendar.month}-${day}`).isAfter(dayjs(this.maxDate), 'day') || dayjs(`${this.calendar.year}-${this.calendar.month}-${day}`).isBefore(dayjs(this.minDate), 'day')))
        || (blocked && this.availableDates.length > 0 && !this.availableDates.includes(dayjs(`${this.calendar.year}-${this.calendar.month}-${day}`).format('YYYY-MM-DD')));
    },

    setCalendarDate(date: Dayjs): void {
      this.calendar.month = date.format('MM');
      this.calendar.year = date.format('YYYY');
      this.calendar.days = getDaysInMonth(date);
    },

    close(): void {
      if (this.$refs.calendarDropdown) {
        (this.$refs.calendarDropdown as BDropdown).hide(true);
      }
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

$colCalendarDim: 40px;
$rowCalendarWidth: calc(#{$colCalendarDim} * 7 + 27px);
label sup, .label sup {
  margin-top: 9px;
}
.rs-form-calendar-custom {
  .form-control.disable-typing {
    &:disabled {
      background-color: $white;
    }
  }
  .input-group-append {
    .btn-group {
      button {
        background-color: #0b64e0 !important;
        border-color: #0b64e0 !important;
      }
    }
  }

  .not-allowed {
    cursor: not-allowed !important;
  }

  .row-calendar {
    width: $rowCalendarWidth;
    z-index: 600;
  }
  @-moz-document url-prefix() {
    .ffCalendar {
      max-width: 40% !important;
    }
  }

  .container-calendar {
    display: flex;
    flex-wrap: wrap;
  }

  .rs-select .custom-select {
    outline: 1px solid $tertiary-muted;
  }

  .rs-button--light.rs-button--hollow {
    border-color: $tertiary-muted;
  }

  .col-calendar {
    width: $colCalendarDim;
    height: $colCalendarDim;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: -1px 0 0 -1px;
  }

  .col-calendar-weekday {
    width: $colCalendarDim;
    text-align:center;
    line-height: 1;
    margin: -1px 0 0 -1px;
    display: inline-block;
  }

  .helper {
    font-size: 0.875rem;
  }

  .icon-only-input-group {
    .input-group-append {
      .dropdown {
          .btn {
          border-radius: 4px;
        }
      }
    }
  }
  .dt-calendar-absolute {
    position:absolute;
    top:-1px;
    width:85%;
  }
}
.footer-text {
  font-size: 0.8rem;
  text-align: left;
}
</style>
